// custom-widgets/GoogleStreetView.ts
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Expand from "@arcgis/core/widgets/Expand";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Color from "@arcgis/core/Color";

type Options = {
    iconUrl?: string;
    startAtCenter?: boolean;
    resizable?: boolean;
    initialSize?: { width: number; height: number };
    minSize?: { width: number; height: number };
    maxSize?: { width: number; height: number };
    attachToExpand?: Expand;
    googleApiKey?: string;
};

export function googleStreetView(view: MapView, opts: Options) {
    const isMobile = window.innerWidth <= 768;
    const defaultInitialSize = isMobile
        ? { width: 250, height: 220 }
        : { width: 420, height: 280 };
    const defaultMinSize = isMobile
        ? { width: 250, height: 220 }
        : { width: 420, height: 280 };
    const {
        iconUrl,
        startAtCenter = true,
        resizable = true,
        initialSize = defaultInitialSize,
        minSize = defaultMinSize,
        maxSize,
        attachToExpand,
        googleApiKey,
    } = opts;

    let mapRef: Map | null = view.map ?? null;

    let usingKeyless = !googleApiKey;

    const layer = new GraphicsLayer({ title: "Google Street View (pin)", listMode: "hide" });
    if (mapRef) {
        mapRef.add(layer);
    } else {
        const mapWatch = view.watch("map", (m: any) => {
            if (m) {
                mapRef = m;
                mapRef?.add(layer);
                mapWatch.remove();
            }
        });
    }

    let pinSymbol: PictureMarkerSymbol | SimpleMarkerSymbol = new SimpleMarkerSymbol({
        color: new Color([0, 121, 193, 1]),
        size: 16,
        outline: new SimpleLineSymbol({ color: new Color([255, 255, 255, 1]), width: 2 }),
    });

    let currentPoint: Point | null = null;
    let pinGraphic: Graphic | null = null;
    let dragging = false;
    let visible = attachToExpand ? !!attachToExpand.expanded : true;

    if (iconUrl) {
        const img = new Image();
        img.onload = () => {
            pinSymbol = new PictureMarkerSymbol({
                url: iconUrl!,
                width: Math.max(24, img.naturalWidth || 32),
                height: Math.max(24, img.naturalHeight || 32),
                yoffset: (img.naturalHeight || 32) / 2,
            });
            if (pinGraphic) pinGraphic.symbol = pinSymbol as any;
            else if (currentPoint && visible) setMarker(currentPoint);
        };
        img.onerror = () => { };
        img.src = iconUrl;
    }

    // Root container — single row; no reserved header height
    const root = document.createElement("div");
    root.className = "esri-widget";
    Object.assign(root.style, {
        display: "grid",
        gridTemplateRows: "1fr",
        width: `${initialSize.width}px`,
        height: `${initialSize.height}px`,
        minWidth: `${minSize.width}px`,
        minHeight: `${minSize.height}px`,
        ...(maxSize ? { maxWidth: `${maxSize.width}px`, maxHeight: `${maxSize.height}px` } : {}),
        resize: resizable ? "both" : "none",
        overflow: "hidden",
        boxSizing: "border-box",
    } as CSSStyleDeclaration);

    // Street View frame wrapper + iframe
    const frameWrap = document.createElement("div");
    Object.assign(frameWrap.style, {
        position: "relative",
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        overflow: "hidden",
        height: "100%",
    });

    const iframe = document.createElement("iframe");
    iframe.allowFullscreen = false;
    iframe.referrerPolicy = "no-referrer-when-downgrade";
    Object.assign(iframe.style, {
        position: "absolute",
        inset: "0",
        width: "100%",
        height: "100%",
        border: "0",
        display: "block",
    });

    frameWrap.appendChild(iframe);
    root.appendChild(frameWrap);

    const alert = document.createElement("div");
    alert.textContent = "Current location is not visible on the map.";
    alert.className = "esri-widget";
    Object.assign(alert.style, {
        position: "absolute",
        top: "8px",
        left: "8px",
        right: "8px",
        zIndex: "10",
        display: "none",
        padding: "8px 12px",
        background: "#fffbe6",
        border: "1px solid #ffe58f",
        color: "#8d6b00",
        borderRadius: "6px",
        fontSize: "12px",
        pointerEvents: "auto",
    });

    const recenter = document.createElement("button");
    recenter.textContent = "Reset";
    Object.assign(recenter.style, {
        marginLeft: "6px",
        border: "none",
        background: "transparent",
        color: "#005fb8",
        cursor: "pointer",
        textDecoration: "underline",
        fontSize: "12px",
    });
    recenter.onclick = () => {
        const pt = view.extent?.center ?? view.center;
        updateStreetView(pt as Point);
        hideAlert();
    };
    alert.appendChild(recenter);

    Object.assign(frameWrap.style, {
        position: "relative",
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        overflow: "hidden",
        height: "100%",
    });

    iframe.allowFullscreen = false;
    iframe.referrerPolicy = "no-referrer-when-downgrade";
    Object.assign(iframe.style, {
        width: "100%",
        height: "100%",
        border: "0",
        display: "block",
    });

    frameWrap.appendChild(iframe);
    root.append(alert, frameWrap);

    let currentSize = { w: initialSize.width, h: initialSize.height };
    let resizeRAF: number | null = null;
    let resizeTimeout: number | null = null;

    function beginResizeSession() {
        iframe.style.pointerEvents = "none";
    }
    function endResizeSession() {
        iframe.style.pointerEvents = "";
    }

    function showAlert() {
        if (visible) alert.style.display = "block";
    }
    function hideAlert() {
        alert.style.display = "none";
    }

    function setMarker(point: Point) {
        if (!visible) return;
        if (!pinGraphic) {
            pinGraphic = new Graphic({
                geometry: point,
                symbol: pinSymbol as any,
            });
            layer.add(pinGraphic);
        } else {
            pinGraphic.geometry = point;
            pinGraphic.symbol = pinSymbol as any;
            (pinGraphic as any).zIndex = 99999;
        }
    }

    function clearMarker() {
        try {
            layer.removeAll();
        } catch { }
        pinGraphic = null;
    }

    function inCurrentView(p?: Point | null) {
        return !!(p && view.extent && view.extent.contains(p));
    }

    /** ---------- Street View via srcdoc + events -> parent ---------- */
    let panoReady = false;
    let messageListener: ((e: MessageEvent) => void) | null = null;

    function ensurePanoIframe(lat: number, lon: number) {
        if (iframe.dataset.panoReady === "1") return;

        if (usingKeyless) {
            iframe.src = embedUrlFor(lat, lon);
            iframe.onload = () => {
                iframe.dataset.panoReady = "1";
                panoReady = true;
            };
            return; // ← no JS API, no message listener
        }

        if (!googleApiKey) {
            iframe.srcdoc = `
        <!doctype html><meta charset="utf-8">
        <style>html,body{height:100%;margin:0;display:grid;place-items:center;font-family:system-ui}</style>
        <div>Street View requires a Google Maps API key.<br/><small>Pass <code>googleApiKey</code> to googleStreetView(...)</small></div>`;
            iframe.onload = () => {
                iframe.dataset.panoReady = "1";
                panoReady = false;
            };
            return;
        }

        const html = String.raw`<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <style>html,body,#p{height:100%;margin:0}#p{position:relative}</style>
</head>
<body>
  <div id="p"></div>
  <script src="https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleApiKey)}"></script>
  <script>
    let pano;
    function init(lat, lng){
      pano = new google.maps.StreetViewPanorama(
        document.getElementById('p'),
        { position:{lat,lng}, pov:{heading:0, pitch:0}, visible:true }
      );
      // Stream events to parent
      pano.addListener('position_changed', () => {
        const pos = pano.getPosition();
        parent.postMessage({ type:'sv-pos', lat: pos.lat(), lng: pos.lng() }, '*');
      });
      pano.addListener('pov_changed', () => {
        const pov = pano.getPov();
        parent.postMessage({ type:'sv-pov', heading: pov.heading, pitch: pov.pitch }, '*');
      });
    }
    init(${lat}, ${lon});

    // Parent -> iframe messages
    addEventListener('message', (e) => {
      const msg = e.data || {};
      if(msg.type === 'setPos' && pano){
        pano.setPosition({lat: msg.lat, lng: msg.lng});
        setTimeout(() => google.maps.event.trigger(pano, 'resize'), 0);
      } else if (msg.type === 'forceResize' && pano){
        google.maps.event.trigger(pano, 'resize');
      }
    });

    // React to own size changes (CSS resize handle)
    new ResizeObserver(() => {
      if(pano) google.maps.event.trigger(pano, 'resize');
    }).observe(document.documentElement);
  </script>
</body>
</html>`;

        iframe.srcdoc = html;
        iframe.onload = () => {
            iframe.dataset.panoReady = "1";
            panoReady = true;
        };

        // Parent listener for sv-pos/sv-pov
        if (!messageListener) {
            messageListener = (e: MessageEvent) => {
                if (e.source !== iframe.contentWindow) return;
                const msg = (e.data || {}) as any;
                if (msg.type === "sv-pos") {
                    // Update ArcGIS pin in real time (convert to WM)
                    const ptGeo = new Point({ x: msg.lng, y: msg.lat, spatialReference: { wkid: 4326 } });
                    const ptWM = webMercatorUtils.geographicToWebMercator(ptGeo) as Point;
                    currentPoint = ptWM;
                    setMarker(ptWM);
                    inCurrentView(ptWM) ? hideAlert() : showAlert();
                } else if (msg.type === "sv-pov") {
                    // Optional: use msg.heading/msg.pitch (e.g., rotate a directional symbol)
                }
            };
            window.addEventListener("message", messageListener);
        }
    }

    function postToPano(msg: any) {
        if (usingKeyless) return;
        try { iframe.contentWindow?.postMessage(msg, "*"); } catch { }
    }

    function updateStreetView(point: Point) {
        currentPoint = point;
        if (visible) setMarker(point);

        const g = webMercatorUtils.webMercatorToGeographic(point) as Point;
        const lat = +(g.y ?? 0);
        const lon = +(g.x ?? 0);

        ensurePanoIframe(lat, lon);

        if (usingKeyless) {
            iframe.src = embedUrlFor(lat, lon);
        } else if (panoReady) {
            postToPano({ type: "setPos", lat, lng: lon });
        }

        if (visible) (inCurrentView(currentPoint) ? hideAlert() : showAlert());
    }

    function embedUrlFor(lat: number, lon: number, heading = 0, pitch = 0, fov = 80) {
        return `https://maps.google.com/maps?q=&layer=c&cbll=${lat},${lon}&cbp=11,${heading},0,0,${pitch}&hl=en&output=svembed`;
    }

    function setVisible(v: boolean) {
        visible = v;
        layer.visible = v;
        if (!v) {
            hideAlert();
            clearMarker();
        } else {
            const center = (view.extent?.center ?? view.center) as Point;
            updateStreetView(center);
        }
    }

    if (attachToExpand) {
        setVisible(!!attachToExpand.expanded);
        attachToExpand.watch("expanded", (val: boolean) => setVisible(!!val));
    }

    view.when(() => {
        const startPoint = startAtCenter ? (view.extent?.center ?? view.center) : view.center;
        updateStreetView(startPoint as Point);
    });

    const clickHandle = view.on("click", (evt: any) => {
        if (!visible) return;
        updateStreetView(evt.mapPoint as Point);
    });

    const extentHandle = view.watch("extent", () => {
        if (!visible || !currentPoint) return;
        inCurrentView(currentPoint) ? hideAlert() : showAlert();
    });

    const downHandle = view.on("pointer-down", async (evt: any) => {
        if (!visible) return;
        const hit = await view.hitTest(evt);
        const onPin = hit.results?.some((r: any) => r.type === "graphic" && r.graphic === pinGraphic);
        dragging = !!onPin;
        if (dragging && view.container instanceof HTMLElement) view.container.style.cursor = "grabbing";
    });

    const moveHandle = view.on("drag", (evt: any) => {
        if (!visible || !dragging) return;
        evt.stopPropagation();
        const mp = view.toMap({ x: evt.x, y: evt.y }) as Point;
        if (mp) setMarker(mp);
    });

    const upHandle = view.on("pointer-up", () => {
        if (!visible) return;
        if (dragging && pinGraphic?.geometry) updateStreetView(pinGraphic.geometry as Point);
        dragging = false;
        if (view.container instanceof HTMLElement) view.container.style.cursor = "default";
    });

    // On widget resize: just nudge pano to repaint (no reloads)
    const ro = new ResizeObserver((entries) => {
        const cr = entries[0]?.contentRect;
        if (!cr) return;
        currentSize = { w: Math.round(cr.width), h: Math.round(cr.height) };
        beginResizeSession();
        if (resizeRAF) cancelAnimationFrame(resizeRAF);
        resizeRAF = requestAnimationFrame(() => {
            if (resizeTimeout) window.clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => {
                postToPano({ type: "forceResize" });
                endResizeSession();
            }, 120);
        });
    });
    ro.observe(root);

    return {
        element: root,
        setLocation(point: Point) {
            updateStreetView(point);
        },
        setSize(width: number, height: number) {
            const w = Math.max(width, minSize.width);
            const h = Math.max(height, minSize.height);
            root.style.width = `${w}px`;
            root.style.height = `${h}px`;
            postToPano({ type: "forceResize" });
        },
        getSize() {
            return { ...currentSize };
        },
        setVisible,
        destroy() {
            try { ro.disconnect(); } catch { }
            try {
                clickHandle.remove();
                extentHandle.remove();
                downHandle.remove();
                moveHandle.remove();
                upHandle.remove();
            } catch { }
            if (messageListener) {
                window.removeEventListener("message", messageListener);
                messageListener = null;
            }
            if (mapRef) { try { mapRef.remove(layer); } catch { } }
            pinGraphic = null;
            if (root.parentElement) root.parentElement.removeChild(root);
        },
    };
}
